import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  InputLabel,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  useTheme,
  Grid2,
  Tabs,
  Tab,
  Stack,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useStores } from "../../use-stores";
import DeleteConfirmation from "../../../../views/components/common/pop-up/DeleteConfirmation";
import ShipmentsTable from "../components/universal-shipment/ShipmentsTable";
import EditShipmentModal from "../components/universal-shipment/EditShipmentsPopup";
import ShipmentActivityLogsDrawer from "../components/universal-shipment/ShipmentActivityLogsDrawer";
import FilterComponent from "../components/universal-shipment/FilterShipmentComponent";
import ShipmentStatusPopup from "../components/universal-shipment/StatusPopup";
import TrackingCsvUploadPopup from "../components/universal-shipment/TrackingCsvUploadPopup";
import ShipmentLinesCsvUploadPopup from "../components/universal-shipment/ShipmentLinesCsvUploadPopup";
import JobRecordsTable from "../components/tables/JobRecordsTable";
import InlineFormError from "../../../../views/components/common/form/InlineFormError";
import { useNotification } from "../../../../utils/NotificationsContext";
import AddPacksPopup from "../components/universal-shipment/AddPacksPopup";
import CreateShipmentWizardPopup from "../components/universal-shipment/CreateShipmentWizardPopup";
import {
  ShipmentBookingType,
  ShipmentMethod,
  SHIPMENT_BOOKING_TYPE_OPTIONS,
  parseDeclaredValue,
  parseBulkShippingCost,
  MAX_BULK_SHIPMENT_IDS,
} from "../../../../models/Shipments";
import {
  AddShipmentItemLineRequest,
  AddShipmentPackLineRequest,
} from "../../../../models/Shipments/ShipmentInventoryLines";
import PermissionBlockLabel from "../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../models/Employees/EmployeeRoles";
import RemovePacksConfirmationPopup from "../../../../views/components/common/pop-up/RemovePacksFromShipment";
import CancelShipmentPopup from "../components/universal-shipment/CancelShipmentPopup";
import BulkCancelShipmentPopup from "../components/universal-shipment/BulkCancelShipmentPopup";
import BulkRemovePacksPopup from "../components/universal-shipment/BulkRemovePacksPopup";
import { ShipmentStatusEnum } from "../../../../models/Shipments/ShipmentStatus";
import { FilterValue } from "../../../../models/Filters/FilterValue";
import CustomTypography from "../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../views/components/common/buttons/AppButton";
import NumericTextField from "../../../../views/components/common/pop-up/NumericTextField";
import AppSearchDropdownMulti from "../../../../views/components/common/filter/AppSearchDropdownMulti";
import CarriersSearchDropdown from "../components/universal-shipment/CarriersSearchDropdown";
import { listingStatusTabsStyles } from "../../../../styles/listingStatusTabsSx";
import { Dayjs } from "dayjs";
import { GetPacksParams } from "../../stores/ShipmentStore";
import {
  getBookingSyncErrorMessage,
  getBulkBookingSyncMessage,
} from "../../../../models/Shipments/ShipmentBooking";
import {
  getBulkAddShipmentBoxesSummary,
  getFilledShipmentBoxPayloads,
} from "../../../../models/Shipments/ShipmentBoxes";
import { canAddShipmentInventoryLines } from "../../helpers/shipmentInventoryLines.helper";

const Status = [
  { id: 1, value: "New" },
  { id: 2, value: "Ready To Ship" },
  { id: 3, value: "Shipped" },
  { id: 4, value: "Out For Delivery" },
  { id: 5, value: "Delivered" },
  { id: 7, value: "Returned" },
  { id: 8, value: "Cancelled" },
];

const SHIPMENT_METHOD_OPTIONS: ShipmentMethod[] = [
  ShipmentMethod.RAIL,
  ShipmentMethod.SURFACE,
  ShipmentMethod.AIR,
];

const textFieldSx = {
  "& .MuiInputLabel-root": { fontSize: "12px" },
  "& .MuiInputBase-input": { fontSize: "12px" },
};

type BulkBoxDraft = {
  key: number;
  length: string;
  breadth: string;
  height: string;
  weight: string;
};

const BULK_BOX_DIMENSION_FIELDS: Array<{
  field: keyof Omit<BulkBoxDraft, "key">;
  label: string;
}> = [
  { field: "length", label: "Length (cm)" },
  { field: "breadth", label: "Breadth (cm)" },
  { field: "height", label: "Height (cm)" },
  { field: "weight", label: "Weight (kg)" },
];

type BulkCancelScope = "selected" | "filters";

type BulkCancelJobState = {
  jobId: number;
  status: string;
  scope: BulkCancelScope;
  totalCount: number;
  processedCount: number;
  cancelledCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
};

type BulkCancelDialogState = {
  open: boolean;
  scope: BulkCancelScope;
  shipmentIds: number[];
  filterCount: number;
  skippedCount: number;
  reason: string;
  submitting: boolean;
  error: string | null;
  job: BulkCancelJobState | null;
  watchJobId: number | null;
};

const emptyBulkCancelDialog = (): BulkCancelDialogState => ({
  open: false,
  scope: "selected",
  shipmentIds: [],
  filterCount: 0,
  skippedCount: 0,
  reason: "",
  submitting: false,
  error: null,
  job: null,
  watchJobId: null,
});

const bulkCancelTerminal = (status: string) =>
  status === "COMPLETED" || status === "FAILED";

type BulkRemovePacksScope = "selected" | "filters";

type BulkRemovePacksJobState = {
  jobId: number;
  status: string;
  scope: BulkRemovePacksScope;
  totalCount: number;
  processedCount: number;
  removedCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
};

type BulkRemovePacksDialogState = {
  open: boolean;
  scope: BulkRemovePacksScope;
  shipmentIds: number[];
  filterCount: number;
  submitting: boolean;
  error: string | null;
  job: BulkRemovePacksJobState | null;
  watchJobId: number | null;
};

const emptyBulkRemovePacksDialog = (): BulkRemovePacksDialogState => ({
  open: false,
  scope: "selected",
  shipmentIds: [],
  filterCount: 0,
  submitting: false,
  error: null,
  job: null,
  watchJobId: null,
});

const bulkRemovePacksTerminal = (status: string) =>
  status === "COMPLETED" || status === "FAILED";


const createEmptyBulkBoxDraft = (() => {
  let nextKey = 1;
  return (): BulkBoxDraft => ({
    key: nextKey++,
    length: "",
    breadth: "",
    height: "",
    weight: "",
  });
})();

// Tabs for shipment status filtering
const RUNNING_JOBS_POLL_MS = 10000;

const SHIPMENT_STATUS_TABS = [
  { id: "all", label: "All", statusIds: [] },
  { id: "new", label: "New", statusIds: [ShipmentStatusEnum.NEW] },
  {
    id: "ready",
    label: "Ready To Ship",
    statusIds: [ShipmentStatusEnum.READY_TO_SHIP],
  },
  {
    id: "delayed",
    label: "Delayed Delivery",
    statusIds: [
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.OUT_FOR_DELIVERY,
    ],
  },
  {
    id: "shipped",
    label: "Shipped",
    statusIds: [
      ShipmentStatusEnum.SHIPPED,
      ShipmentStatusEnum.OUT_FOR_DELIVERY,
    ],
  },
  {
    id: "delivered",
    label: "Delivered",
    statusIds: [ShipmentStatusEnum.DELIVERED],
  },
  {
    id: "rto",
    label: "RTO",
    statusIds: [
      ShipmentStatusEnum.RETURN_REQUESTED,
      ShipmentStatusEnum.RETURNED,
    ],
  },
  {
    id: "failed",
    label: "Failed/Cancelled",
    statusIds: [
      ShipmentStatusEnum.CANCELLED,
      ShipmentStatusEnum.UNDELIVERED,
      ShipmentStatusEnum.DAMAGED,
    ],
  },
];

const getTabById = (tabId?: string | null) =>
  SHIPMENT_STATUS_TABS.find((tab) => tab.id === tabId);

const getApiSuccessMessage = (
  res: { message?: string; result?: unknown } | null | undefined,
  fallback: string,
): string => {
  if (typeof res?.message === "string" && res.message.trim()) {
    return res.message;
  }
  const result = res?.result;
  if (typeof result === "string" && result.trim()) {
    return result;
  }
  if (result && typeof result === "object" && "updatedCount" in result) {
    const updatedCount = Number(
      (result as { updatedCount?: number }).updatedCount,
    );
    if (Number.isFinite(updatedCount)) {
      return `Updated ${updatedCount} shipment(s) successfully`;
    }
  }
  return fallback;
};

export interface UniversalShipmentsProps {
  lockedOrderId?: number;
  lockedInventoryType?: string;
  preserveQueryKeys?: string[];
  hidePageHeader?: boolean;
  defaultStatusTab?: string;
  viewPermission?: EmployeeRoles;
  compactLayout?: boolean;
  simpleSearchOnly?: boolean;
  pageHeaderStart?: React.ReactNode;
  headerTrailingActions?: React.ReactNode;
}

const UniversalShipments: React.FC<UniversalShipmentsProps> = ({
  lockedOrderId,
  lockedInventoryType,
  preserveQueryKeys,
  hidePageHeader = false,
  defaultStatusTab,
  viewPermission = EmployeeRoles.VIEW_SHIPMENTS,
  compactLayout = false,
  simpleSearchOnly = false,
  pageHeaderStart,
  headerTrailingActions,
}) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { shipmentStore, userStore } = useStores();
  const { showError, showSuccess, showWarning } = useNotification();
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;

  const searchParams = new URLSearchParams(location.search);
  let clientSku: FilterValue[] = [];
  const clientSkuParam = searchParams.get("clientSku");
  if (clientSkuParam) {
    try {
      const parsed = JSON.parse(clientSkuParam);
      clientSku = Array.isArray(parsed) ? parsed : [];
    } catch {
      clientSku = clientSkuParam
        .split(",")
        .map((sku) => sku.trim())
        .filter(Boolean)
        .map((sku) => ({ id: sku, value: sku }));
    }
    clientSku = clientSku.slice(0, 20);
  }
  const fallbackTabId = defaultStatusTab || "new";
  const fallbackTab = getTabById(fallbackTabId) || SHIPMENT_STATUS_TABS[1];
  const statusFromUrl = searchParams.get("status");
  const initialStatus = statusFromUrl
    ? statusFromUrl.split(",").map(Number)
    : fallbackTab.statusIds;
  const initialTabIdFromStatus =
    getTabById(searchParams.get("tab_id"))?.id ||
    SHIPMENT_STATUS_TABS.find(
      (tab) =>
        tab.statusIds.length === initialStatus.length &&
        tab.statusIds.every((id) => initialStatus.includes(id)),
    )?.id ||
    fallbackTab.id;

  const initialParams = {
    page: searchParams.get("page")
      ? parseInt(searchParams.get("page") as string, 10)
      : 1,
    limit: searchParams.get("limit")
      ? parseInt(searchParams.get("limit") as string, 10)
      : 30,
    sortBy: searchParams.get("sort_by") || "created_date",
    sortType: (searchParams.get("sort_type") as "ASC" | "DESC") || "DESC",
    search: searchParams.get("search") || "",
    shipment_type: searchParams.get("shipment_type")
      ? searchParams.get("shipment_type")?.split(",").map(Number)
      : [],
    status: initialStatus,
    tab_id:
      getTabById(searchParams.get("tab_id"))?.id || initialTabIdFromStatus,
    delivery_partner_id: searchParams.get("delivery_partner_id")
      ? searchParams.get("delivery_partner_id")?.split(",").map(Number)
      : [],
    order_id: lockedOrderId
      ? [lockedOrderId]
      : searchParams.get("order_id")
        ? searchParams.get("order_id")?.split(",").map(Number)
        : [],
    dimensions: searchParams.get("dimensions")
      ? searchParams.get("dimensions")?.split(",").map(Number)
      : [],
    c_id: searchParams.get("c_id")
      ? searchParams
          .get("c_id")!
          .split(",")
          .map((item) => {
            const list = item.split(":")!;
            return { id: list[0], value: list[1] };
          })
      : [],
    inventory_type:
      lockedInventoryType || searchParams.get("inventory_type") || "-1",
    clientSku,
    packs_exist:
      searchParams.get("packs_exist") === "true"
        ? true
        : searchParams.get("packs_exist") === "false"
          ? false
          : undefined,
    country: searchParams.get("country")?.split(",") || [],
    search_text_value: searchParams.get("search_text_value")
      ? searchParams
          .get("search_text_value")
          ?.split(",")
          .map((item) => {
            const trimmed = item.trim();
            const num = Number(trimmed);
            return !isNaN(num) && num !== 0 ? num : trimmed;
          })
      : [],
    contact_number: searchParams.get("contact_number") || "",
    allSelected: false,
    start_date: searchParams.get("start_date") || null,
    end_date: searchParams.get("end_date") || null,
  };

  const [params, setParams] = useState(initialParams);
  // const [selectedShipmentIds, setSelectedShipmentIds] = useState<number[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editShipmentId, setEditShipmentId] = useState<number | null>(null);
  const [returnedId, setReturnedId] = useState<number | null>(null);
  const [removePacksFromShipmentId, setRemovePacksFromShipmentId] = useState<
    number | null
  >(null);
  const [allSelected, setAllSelected] = useState<boolean>(false);
  const [clearShipments, setClearShipments] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isCreateShipmentWizardOpen, setIsCreateShipmentWizardOpen] =
    useState(false);
  const [isEditCsvDialogOpen, setIsEditCsvDialogOpen] = useState(false);
  const [isLinesCsvDialogOpen, setIsLinesCsvDialogOpen] = useState(false);
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [isBookingSyncConfirmOpen, setIsBookingSyncConfirmOpen] =
    useState(false);
  const [isBookingSyncing, setIsBookingSyncing] = useState(false);
  const [bulkBoxDrafts, setBulkBoxDrafts] = useState<BulkBoxDraft[]>(() => [
    createEmptyBulkBoxDraft(),
  ]);
  const [bulkEditFields, setBulkEditFields] = useState({
    delivery_partner_id: -1,
    delivery_partner_name: "",
    shipment_method: "" as ShipmentMethod | "",
    booking_type: "" as ShipmentBookingType | "",
    declared_value: "",
  });
  const [bulkEditSubmitting, setBulkEditSubmitting] = useState(false);
  const [bulkEditInlineMessage, setBulkEditInlineMessage] = useState<{
    severity: "success" | "error" | "info";
    text: string;
  } | null>(null);
  const [bulkShippingCostDialog, setBulkShippingCostDialog] = useState<{
    submitting: boolean;
    error: string | null;
  }>({ submitting: false, error: null });
  const [bulkCancelDialog, setBulkCancelDialog] =
    useState<BulkCancelDialogState>(emptyBulkCancelDialog);
  const [bulkRemovePacksDialog, setBulkRemovePacksDialog] =
    useState<BulkRemovePacksDialogState>(emptyBulkRemovePacksDialog());
  const refreshAfterRemovePacksRef = useRef<() => Promise<void>>(async () => {});
  const [addPacksParams, setAddPacksParams] = useState<GetPacksParams | null>(
    null,
  );
  const actionsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const activeStatusTab = React.useMemo(
    () => getTabById(params.tab_id)?.id || SHIPMENT_STATUS_TABS[0].id,
    [params.tab_id],
  );
  const canBulkUpdateShippingCost =
    activeStatusTab === "new" || activeStatusTab === "ready";

  const abortControllerRef = useRef<AbortController | null>(null);

  const withLockedFilters = useCallback(
    (listingParams: typeof params) => ({
      ...listingParams,
      ...(lockedOrderId ? { order_id: [lockedOrderId] } : {}),
      ...(lockedInventoryType ? { inventory_type: lockedInventoryType } : {}),
    }),
    [lockedOrderId, lockedInventoryType],
  );

  const handleStatusTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
  ) => {
    // Cancel in-flight API request when tab changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const selectedTab = getTabById(newValue) || SHIPMENT_STATUS_TABS[0];
    setParams((prev) => ({
      ...prev,
      tab_id: selectedTab.id,
      status: selectedTab.statusIds,
      page: 1,
    }));
  };

  const [selectedShipmentIdsMap, setSelectedShipmentIdsMap] = useState<
    Map<number, number>
  >(new Map<number, number>());

  const fetchShipments = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      const res = await shipmentStore.getShipments(
        withLockedFilters(params),
        false,
        controller.signal,
      );
      if (!res && shipmentStore.error) {
        showErrorRef.current("Error", shipmentStore.error as string);
      }
    } catch (error: any) {
      if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
        return;
      }
      showErrorRef.current("Error", "Error fetching shipments");
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [params, shipmentStore, withLockedFilters]);

  // Listing filters without page / sort / tab, so counts cover every status tab.
  const countFilterParams = React.useMemo(() => {
    const {
      page: _page,
      limit: _limit,
      sortBy: _sortBy,
      sortType: _sortType,
      tab_id: _tabId,
      status: _status,
      allSelected: _allSelected,
      ...filters
    } = withLockedFilters(params);
    return filters;
  }, [params, withLockedFilters]);
  const countFilterKey = JSON.stringify(countFilterParams);

  const refreshCountByStatus = useCallback(() => {
    if (!userStore.hasPermission(viewPermission)) return;
    shipmentStore.getCountByStatus(countFilterParams).catch(() => {});
  }, [userStore, viewPermission, shipmentStore, countFilterParams]);

  const handleRefreshShipments = useCallback(async () => {
    refreshCountByStatus();
    await fetchShipments();
  }, [refreshCountByStatus, fetchShipments]);

  const refreshShipmentsAfterCancel = useCallback(async () => {
    setSelectedShipmentIdsMap(new Map());
    setAllSelected(false);
    await fetchShipments();
    if (!lockedOrderId) {
      refreshCountByStatus();
    }
  }, [fetchShipments, lockedOrderId, refreshCountByStatus]);

  const refreshAfterCancelRef = useRef(refreshShipmentsAfterCancel);
  refreshAfterCancelRef.current = refreshShipmentsAfterCancel;

  const lastLockedOrderIdRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!lockedOrderId) return;

    const orderChanged = lastLockedOrderIdRef.current !== lockedOrderId;
    lastLockedOrderIdRef.current = lockedOrderId;

    setParams((prev) => {
      const prevOrderIds = prev.order_id || [];
      const orderAlreadyLocked =
        prevOrderIds.length === 1 && prevOrderIds[0] === lockedOrderId;
      const inventoryAlreadyLocked =
        !lockedInventoryType || prev.inventory_type === lockedInventoryType;
      if (orderAlreadyLocked && inventoryAlreadyLocked) {
        return prev;
      }
      return {
        ...prev,
        order_id: [lockedOrderId],
        ...(lockedInventoryType ? { inventory_type: lockedInventoryType } : {}),
        page: orderAlreadyLocked ? prev.page : 1,
      };
    });

    if (!orderChanged) return;

    setSelectedShipmentIdsMap(new Map());
    setAllSelected(false);
    shipmentStore.shipments = [];
    shipmentStore.totalCount = 0;
    shipmentStore.countByStatus = [];
  }, [lockedOrderId, lockedInventoryType, shipmentStore]);

  useEffect(() => {
    if (!userStore.hasPermission(viewPermission)) {
      return;
    }
    fetchShipments();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // Only refetch when listing params or lock/permission change.
    // Do not depend on fetchShipments/showError — those change on every
    // notification/store re-render and would loop POST /v5/shipments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, lockedOrderId, lockedInventoryType, viewPermission]);

  // Refetch counts when filters change (Apply), not on page / sort / tab.
  useEffect(() => {
    refreshCountByStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countFilterKey, viewPermission]);

  const hasRunningLinesUploadJobs =
    shipmentStore.runningLinesUploadJobs.length > 0;

  useEffect(() => {
    if (!userStore.hasPermission(viewPermission)) return;
    void shipmentStore.fetchRunningLinesUploadJobs();
  }, [userStore, shipmentStore, viewPermission]);

  // Keep the running jobs fresh so finished ones drop off and the section hides.
  useEffect(() => {
    if (!hasRunningLinesUploadJobs) return;
    const timer = setInterval(() => {
      void shipmentStore.fetchRunningLinesUploadJobs();
    }, RUNNING_JOBS_POLL_MS);
    return () => clearInterval(timer);
  }, [hasRunningLinesUploadJobs, shipmentStore]);

  const preserveQueryKeysSignature = (preserveQueryKeys || []).join(",");

  useEffect(() => {
    setAllSelected(false);

    const nextSearchParams = new URLSearchParams();
    if (preserveQueryKeysSignature) {
      const currentUrlParams = new URLSearchParams(window.location.search);
      preserveQueryKeysSignature.split(",").forEach((key) => {
        const currentValue = currentUrlParams.get(key);
        if (currentValue != null && currentValue !== "") {
          nextSearchParams.set(key, currentValue);
        }
      });
    }
    Object.entries(params).forEach(([key, value]) => {
      if (key !== "emails") {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          const paramValue = Array.isArray(value)
            ? value.join(",")
            : value.toString();
          nextSearchParams.set(key, paramValue);
        }
      }

      if (key === "c_id") {
        const paramValue = params.c_id
          .map((item) => item.id + ":" + item.value)
          .join(",");
        nextSearchParams.set(key, paramValue);
      }

      if (key === "packs_exist") {
        if (params.packs_exist !== undefined) {
          nextSearchParams.set(key, params.packs_exist ? "true" : "false");
        }
      }

      if (key === "country") {
        nextSearchParams.set(key, params.country.join(","));
      }

      if (key === "clientSku") {
        if (value && (!Array.isArray(value) || value.length > 0)) {
          nextSearchParams.set(key, JSON.stringify(value));
        }
      }
    });

    const queryString = nextSearchParams.toString();
    const currentSearch = window.location.search.replace(/^\?/, "");
    if (queryString && queryString !== currentSearch) {
      navigate({ search: queryString }, { replace: true });
    }
  }, [params, navigate, preserveQueryKeysSignature]);

  const handleFilterChange = useCallback(
    (filters: {
      shipmentType: number[];
      deliveryPartner: number[];
      orderIds: number[];
      dimensions: number[];
      emails?: string;
      inventoryType: string;
      clientSkus: FilterValue[];
      packs_exist?: boolean | undefined;
      country?: string[] | undefined;
      search_text_value?: (number | string)[] | undefined;
      contact_number?: string | undefined;
      customers: FilterValue[];
      start_date: Dayjs | null;
      end_date: Dayjs | null;
    }) => {
      if (filters.emails && filters.emails !== "") {
        const emails_list = filters.emails.split(",");
        if (emails_list.length > 200) {
          showError("Error", "200 is the limit of email ids for filters.");
          return;
        }
      }

      // Cancel in-flight request when filters change
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      setParams((prev) => ({
        ...prev,
        shipment_type: filters.shipmentType,
        delivery_partner_id: filters.deliveryPartner,
        order_id: lockedOrderId ? [lockedOrderId] : filters.orderIds,
        dimensions: filters.dimensions,
        emails: filters.emails,
        inventory_type: lockedInventoryType || filters.inventoryType,
        clientSku: (filters.clientSkus || []).slice(0, 20),
        packs_exist: filters.packs_exist,
        country: filters.country || [],
        search_text_value: filters.search_text_value || [],
        contact_number: filters.contact_number || "",
        c_id: filters.customers || [],
        start_date: filters.start_date?.format("YYYY-MM-DD") || null,
        end_date: filters.end_date?.format("YYYY-MM-DD") || null,
        page: 1,
      }));
      // setSelectedShipmentIds([]);
      setSelectedShipmentIdsMap(new Map());
    },
    [lockedOrderId, lockedInventoryType, showError],
  );

  const handleSortChange = (column: string) => {
    const newSortType = params.sortType === "ASC" ? "DESC" : "ASC";
    setParams((prev) => ({
      ...prev,
      sortBy: column,
      sortType: newSortType,
    }));
  };

  const handlePageChange = (newPage: number) => {
    setParams((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setParams((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1,
    }));
  };

  const toggleAllSelected = (selected: boolean) => {
    setAllSelected(selected);
  };

  const handleCancelShipmentConfirm = async (
    shipmentId: number,
    reason: string,
  ) => {
    if (shipmentId) {
      const res = await shipmentStore.cancelShipment(shipmentId, reason);
      setDeleteId(null);
      if (res?.success) {
        showSuccess(res?.message || "Shipment cancelled successfully");
        setSelectedShipmentIdsMap((current) => {
          if (!current.has(shipmentId)) return current;
          const next = new Map(current);
          next.delete(shipmentId);
          return next;
        });
        await fetchShipments();
        if (!lockedOrderId) {
          refreshCountByStatus();
        }
      } else {
        showError(
          "Error Occured",
          shipmentStore.error || "Error cancelling shipment",
        );
      }
    }
  };

  const handleReturnedConfirm = async (shipmentId: number) => {
    if (shipmentId) {
      const res = await shipmentStore.returnShipment(shipmentId);
      setReturnedId(null);
      if (res?.success) {
        showSuccess(getApiSuccessMessage(res, "Shipment marked as returned"));
      } else {
        showError(
          "Error Occured",
          shipmentStore.error || "Error marking shipment as returned",
        );
      }
    }
  };

  const handleRemovePacksConfirm = async (shipmentId: number) => {
    if (shipmentId) {
      const res = await shipmentStore.removePacksFromShipment(shipmentId);
      setRemovePacksFromShipmentId(null);
      if (res?.success) {
        showSuccess(getApiSuccessMessage(res, "Packs removed successfully"));
      } else {
        showError(
          "Error Occured",
          shipmentStore.error ||
            "Failed to remove packs from shipment, please try again later",
        );
      }
    }
  };

  const handleUpdateShipmentStatus = async (
    shipmentId: number,
    status: ShipmentStatusEnum,
  ) => {
    if (status !== ShipmentStatusEnum.NEW) {
      return;
    }
    const res = await shipmentStore.moveShipmentToNew(shipmentId);
    if (res?.success) {
      await fetchShipments();
      refreshCountByStatus();
    } else {
      showError(
        "Error",
        shipmentStore.error ||
          res?.message ||
          "Failed to move shipment to New. Please try again later.",
      );
    }
  };

  useEffect(() => {
    if (userStore.hasPermission(viewPermission)) {
      shipmentStore.listDeliveryPartners();
    }
  }, [shipmentStore, userStore, viewPermission]);

  useEffect(() => {
    if (clearShipments) {
      setClearShipments(false);
    }
  }, [clearShipments]);

  const downloadCsv = async () => {
    try {
      if (allSelected === true) {
        const response = await shipmentStore.downloadBulkShipmentsCSVReport(
          withLockedFilters(params),
        );
        setAllSelected(false);
        setClearShipments(true);
        // setSelectedShipmentIds([]);
        setActivePopup(null);
        // setSelectedShipmentIdsMap(new Map());
        showSuccess(response.message);
      } else {
        await shipmentStore.getShipments(withLockedFilters(params), true);
        setAllSelected(false);
        setClearShipments(true);
        // setSelectedShipmentIds([]);
        setActivePopup(null);
        // setSelectedShipmentIdsMap(new Map());
      }
    } catch (error: any) {
      showError("Error Occured", error?.message || "Error downloading CSV");
    }
  };

  const downloadShiprocketCsv = async () => {
    try {
      if (allSelected === true) {
        const response = await shipmentStore.getShiprocketCSVBulkReport(
          withLockedFilters(params),
        );
        setAllSelected(false);
        setClearShipments(true);
        // setSelectedShipmentIds([]);
        setActivePopup(null);
        // setSelectedShipmentIdsMap(new Map());
        showSuccess(response.message);
      } else {
        await shipmentStore.downloadShiprocketCSV(withLockedFilters(params));
      }
    } catch (error: any) {
      console.log(error);
      showError(
        "Error Occured",
        error?.response?.data?.message ||
          error?.message ||
          "Error downloading CSV",
      );
    }
  };

  const downloadShipmozoCsv = async () => {
    try {
      handleClose();
      if (allSelected === true) {
        const response = await shipmentStore.getShipmozoCSVBulkReport(
          withLockedFilters(params),
        );
        setAllSelected(false);
        setClearShipments(true);
        setActivePopup(null);
        showSuccess(response.message);
      } else {
        await shipmentStore.downloadShipmozoCSV(withLockedFilters(params));
      }
    } catch (error: any) {
      console.log(error);
      showError(
        "Error Occured",
        error?.response?.data?.message ||
          error?.message ||
          "Error downloading Excel",
      );
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleBulkMenu = () => {
    if (actionsAnchorRef.current) {
      setAnchorEl(actionsAnchorRef.current);
    }
  };

  const handleDialogOpen = () => {
    setIsEditCsvDialogOpen(true);
  };

  const handleOpenLinesCsvDialog = () => {
    handleClose();
    setIsLinesCsvDialogOpen(true);
  };

  const handleOpenBookingSyncConfirm = () => {
    handleClose();
    setIsBookingSyncConfirmOpen(true);
  };

  const handleSyncBooking = async () => {
    try {
      setIsBookingSyncing(true);
      const res = await shipmentStore.syncBooking();
      if (res?.success) {
        const message =
          (typeof res.message === "string" && res.message.trim()) ||
          getBulkBookingSyncMessage(res.result);
        if ((res.result?.enqueueFailed || 0) > 0) {
          showWarning(message);
        } else {
          showSuccess(message);
        }
        setParams((prevParams) => ({
          ...prevParams,
          page: 1,
        }));
      } else {
        showError(
          "Error",
          shipmentStore?.error || "Failed to queue shipment booking.",
        );
      }
    } catch (error: unknown) {
      showError("Error", getBookingSyncErrorMessage(error));
    } finally {
      setIsBookingSyncing(false);
      setIsBookingSyncConfirmOpen(false);
    }
  };

  const resetBulkEditFields = () => {
    setBulkBoxDrafts([createEmptyBulkBoxDraft()]);
    setBulkEditFields({
      delivery_partner_id: -1,
      delivery_partner_name: "",
      shipment_method: "",
      booking_type: "",
      declared_value: "",
    });
    setBulkEditSubmitting(false);
    setBulkEditInlineMessage(null);
  };

  const updateBulkBoxDraft = (
    key: number,
    field: keyof Omit<BulkBoxDraft, "key">,
    value: string,
  ) => {
    setBulkBoxDrafts((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
    );
  };

  const addBulkBoxDraft = () => {
    setBulkBoxDrafts((prev) => [...prev, createEmptyBulkBoxDraft()]);
  };

  const removeBulkBoxDraft = (key: number) => {
    setBulkBoxDrafts((prev) => {
      if (prev.length <= 1) {
        return [createEmptyBulkBoxDraft()];
      }
      return prev.filter((row) => row.key !== key);
    });
  };

  const handleBulkUpdate = async (
    {
      status,
      deliveryPartnerId,
      deliveryPartnerName,
      shipmentMethod,
      bookingType,
      declaredValue,
    }: {
      status?: number;
      deliveryPartnerId?: number;
      deliveryPartnerName?: string;
      shipmentMethod?: ShipmentMethod;
      bookingType?: ShipmentBookingType;
      declaredValue?: number | null;
    },
    options?: { notify?: boolean },
  ) => {
    const notify = options?.notify !== false;
    const selectedIds = Array.from(selectedShipmentIdsMap.keys());
    try {
      const res = await shipmentStore.bulkShipmentUpdate(
        withLockedFilters({ ...params, allSelected: allSelected }),
        selectedIds,
        undefined,
        undefined,
        undefined,
        undefined,
        status,
        deliveryPartnerId,
        shipmentMethod,
        undefined,
        deliveryPartnerName,
        bookingType,
        declaredValue,
      );

      if (res?.success) {
        setAllSelected(false);
        setClearShipments(true);
        if (notify) {
          showSuccess(getApiSuccessMessage(res, "Saved successfully"));
        }
        return true;
      }

      setClearShipments(true);
      if (notify) {
        showError("Error Occurred", shipmentStore.error || "Error saving data");
      }
      return false;
    } catch (error: unknown) {
      setClearShipments(true);
      if (notify) {
        showError(
          "Error Occurred",
          (error as { message?: string })?.message ||
            shipmentStore.error ||
            "Error saving data",
        );
      }
      return false;
    }
  };

  const handlePopupSubmit = async (type: string, value: any) => {
    if (type === "status") {
      const saved = await handleBulkUpdate({ status: value });
      if (saved) {
        setActivePopup(null);
      }
    }
  };

  const handleBulkEditSubmit = async () => {
    const { delivery_partner_id, shipment_method, booking_type } =
      bulkEditFields;
    const { payloads: boxPayloads, partialRowNumbers } =
      getFilledShipmentBoxPayloads(bulkBoxDrafts);
    const hasAnyPackageField =
      boxPayloads.length > 0 || partialRowNumbers.length > 0;
    const hasBookingType = Boolean(booking_type);
    const hasAnyPartnerField =
      delivery_partner_id !== -1 || Boolean(shipment_method) || hasBookingType;
    const hasAllPartnerFields =
      delivery_partner_id !== -1 && Boolean(shipment_method) && hasBookingType;
    const declaredResult = parseDeclaredValue(bulkEditFields.declared_value);
    if (!declaredResult.ok) {
      setBulkEditInlineMessage({
        severity: "error",
        text: declaredResult.message,
      });
      return;
    }
    const declaredValueToSend =
      bulkEditFields.declared_value.trim() === ""
        ? undefined
        : declaredResult.declaredValue;
    const hasDeclaredValue = declaredValueToSend != null;

    if (!hasAnyPackageField && !hasAnyPartnerField && !hasDeclaredValue) {
      setBulkEditInlineMessage({
        severity: "error",
        text: "Add boxes, partner details, and/or declared value before saving.",
      });
      return;
    }

    if (partialRowNumbers.length > 0) {
      const rowLabel =
        partialRowNumbers.length === 1
          ? `Box ${partialRowNumbers[0]}`
          : `Boxes ${partialRowNumbers.join(", ")}`;
      setBulkEditInlineMessage({
        severity: "error",
        text: `${rowLabel}: length, breadth, height, and weight must all be greater than 0.`,
      });
      return;
    }

    if (hasAnyPartnerField && !hasAllPartnerFields) {
      setBulkEditInlineMessage({
        severity: "error",
        text: "Shipment method, delivery partner, and booking type must be filled together.",
      });
      return;
    }

    const selectedIds = Array.from(selectedShipmentIdsMap.keys());
    if (boxPayloads.length > 0 && selectedIds.length === 0) {
      setBulkEditInlineMessage({
        severity: "error",
        text: "Select specific shipments to add boxes. Filter-wide select all is not supported because boxes are added one shipment at a time.",
      });
      return;
    }

    setBulkEditSubmitting(true);
    setBulkEditInlineMessage(null);

    try {
      const summaryParts: string[] = [];
      let boxFailed = false;
      let partnerFailed = false;

      if (boxPayloads.length > 0) {
        const boxResult = await shipmentStore.addShipmentBoxToShipments(
          selectedIds,
          boxPayloads,
        );
        summaryParts.push(
          getBulkAddShipmentBoxesSummary(boxResult, selectedIds.length),
        );
        if (boxResult.failed.length > 0) {
          boxFailed = true;
        }
      }

      if (hasAllPartnerFields || hasDeclaredValue) {
        const partnerSaved = await handleBulkUpdate(
          {
            ...(hasAllPartnerFields && {
              deliveryPartnerId: delivery_partner_id,
              deliveryPartnerName: bulkEditFields.delivery_partner_name,
              shipmentMethod: shipment_method as ShipmentMethod,
              bookingType: booking_type as ShipmentBookingType,
            }),
            ...(hasDeclaredValue && { declaredValue: declaredValueToSend }),
          },
          { notify: false },
        );
        if (!partnerSaved) {
          partnerFailed = true;
          summaryParts.push(
            shipmentStore.error || "Shipment details could not be saved.",
          );
        } else {
          if (hasAllPartnerFields) {
            summaryParts.push("Partner details updated.");
          }
          if (hasDeclaredValue) {
            summaryParts.push("Declared value updated.");
          }
        }
      }

      const summary = summaryParts.join(" ");
      if (boxFailed || partnerFailed) {
        setBulkEditInlineMessage({
          severity:
            boxPayloads.length > 0 && !boxFailed && partnerFailed
              ? "info"
              : "error",
          text: summary,
        });
        return;
      }

      showSuccess(summary || "Saved successfully");
      setActivePopup(null);
      resetBulkEditFields();
    } catch (error: unknown) {
      setBulkEditInlineMessage({
        severity: "error",
        text:
          (error as { message?: string })?.message ||
          "Failed to update selected shipments.",
      });
    } finally {
      setBulkEditSubmitting(false);
    }
  };

  const canAddLinesToShipment = (status?: number | null) =>
    canAddShipmentInventoryLines({
      status,
      hasEditShipments: userStore.hasPermission(EmployeeRoles.EDIT_SHIPMENTS),
      hasEditTerminalLines: userStore.hasPermission(
        EmployeeRoles.EDIT_ITEMS_FROM_TERMINAL_SHIPMENTS,
      ),
    });

  const openAddPacksPopup = (shipmentIdsMap: Map<number, number>) => {
    const shipments = shipmentStore.shipments?.filter((shipment) =>
      shipmentIdsMap.has(shipment.id),
    );

    const blockedShipment = shipments?.find(
      (shipment) => !canAddLinesToShipment(shipment.status),
    );
    if (blockedShipment) {
      showError(
        "Error",
        "Packs and items can only be added on New or Ready To Ship shipments. Shipped or Delivered shipments need the terminal-line permission.",
      );
      return;
    }

    const shipmentInventoryMap = new Map<number, number>();
    shipments?.forEach((shipment) => {
      if (shipment.id && shipment.inventory_id) {
        shipmentInventoryMap.set(shipment.id, shipment.inventory_id);
      }
    });

    const selectedInventoryIds = Array.from(shipmentIdsMap.keys())
      .map((id) => shipmentInventoryMap.get(id))
      .filter((id): id is number => id !== undefined);

    const uniqueInventoryIds = new Set(selectedInventoryIds);

    if (uniqueInventoryIds.size === 0) {
      showError("Error", "Shipment does not have an inventory assigned.");
      return;
    }

    if (uniqueInventoryIds.size > 1) {
      showError("Error", "Select shipments with the same Inventory Id");
      return;
    }

    const inventoryId = Array.from(uniqueInventoryIds)[0];

    const getPacksParams: GetPacksParams = {
      shipment_ids: Array.from(shipmentIdsMap.keys()),
      inventory_id: inventoryId,
    };
    setAddPacksParams(getPacksParams);
    setActivePopup("addPacks");
  };

  const handleAddPacksForShipment = (shipment_id: number) => {
    const shipment = shipmentStore.shipments?.find((s) => s.id === shipment_id);
    const inventoryId = shipment?.inventory_id;

    if (!canAddLinesToShipment(shipment?.status)) {
      showError(
        "Error",
        "Packs and items cannot be added in this shipment status.",
      );
      return;
    }

    if (!inventoryId) {
      showError("Error", "Shipment does not have an inventory assigned.");
      return;
    }

    const getPacksParams: GetPacksParams = {
      shipment_ids: [shipment_id],
      inventory_id: inventoryId,
    };
    const map = new Map();
    map.set(shipment_id, shipment_id);
    setSelectedShipmentIdsMap(map);

    setAddPacksParams(getPacksParams);
    setActivePopup("addPacks");
  };

  const resetBulkShippingCost = () => {
    shipmentStore.clearBulkShippingCostDraft();
    setBulkShippingCostDialog({ submitting: false, error: null });
  };

  const openBulkShippingCostPopup = () => {
    // The API takes explicit ids, so "select all matching filters" is not supported here.
    if (allSelected) {
      showError(
        "Select shipments",
        `Bulk shipping cost works on selected shipments only. Select up to ${MAX_BULK_SHIPMENT_IDS} shipments instead of "select all".`,
      );
      return;
    }
    if (selectedShipmentIdsMap.size === 0) {
      showError("Select shipments", "Select at least one shipment first.");
      return;
    }
    if (selectedShipmentIdsMap.size > MAX_BULK_SHIPMENT_IDS) {
      showError(
        "Too many shipments",
        `You can update up to ${MAX_BULK_SHIPMENT_IDS} shipments at a time.`,
      );
      return;
    }
    resetBulkShippingCost();
    setActivePopup("bulkShippingCost");
  };

  const handleBulkShippingCostSubmit = async () => {
    const parsed = parseBulkShippingCost(shipmentStore.bulkShippingCostDraft);
    if (!parsed.ok) {
      setBulkShippingCostDialog((current) => ({
        ...current,
        error: parsed.message,
      }));
      return;
    }

    const selectedIds = Array.from(selectedShipmentIdsMap.keys());
    setBulkShippingCostDialog({ submitting: true, error: null });
    try {
      const res = await shipmentStore.bulkUpdateShippingCost(
        selectedIds,
        parsed.cost,
      );
      if (!res?.success) {
        // Keep the dialog open so the user can see why and retry.
        setBulkShippingCostDialog({
          submitting: false,
          error: shipmentStore.error || "Failed to update shipping cost.",
        });
        return;
      }

      const updatedCount = res.result?.updatedCount ?? 0;
      const skippedCount = res.result?.skippedCount ?? 0;
      if (skippedCount > 0) {
        showWarning(
          `${updatedCount} updated, ${skippedCount} skipped (not New or Ready to Ship).`,
        );
      }
      resetBulkShippingCost();
      setActivePopup(null);
      setAllSelected(false);
      setClearShipments(true);
      setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
    } finally {
      setBulkShippingCostDialog((current) =>
        current.submitting ? { ...current, submitting: false } : current,
      );
    }
  };

  const hasAppliedCancelFilter = () => {
    const hasValue = (item: unknown) => {
      if (item == null) return false;
      if (typeof item === "string") return item.trim() !== "";
      if (typeof item === "number") return Number.isFinite(item);
      if (typeof item === "object" && "id" in item) {
        return String((item as { id?: unknown }).id ?? "").trim() !== "";
      }
      return false;
    };
    const hasItems = (value: readonly unknown[] | null | undefined) =>
      (value ?? []).some(hasValue);

    // Filter-scope bulk cancel requires customer and/or order; other filters are optional.
    return hasItems(params.order_id) || hasItems(params.c_id);
  };

  const resumeBulkCancelJob = (job: BulkCancelJobState) => {
    const stillRunning = !bulkCancelTerminal(job.status);
    setBulkCancelDialog({
      ...emptyBulkCancelDialog(),
      open: true,
      scope: job.scope,
      job,
      watchJobId: stillRunning ? job.jobId : null,
    });
  };

  /** Refresh the listing only when the admin closes after a finished job. */
  const closeBulkCancelPopup = () => {
    const current = bulkCancelDialog;
    // Don't dismiss while create is in flight — the confirm handler would reopen.
    if (current.submitting) return;

    const jobFinished =
      current.job != null && bulkCancelTerminal(current.job.status);
    const keepRunning =
      current.watchJobId != null &&
      current.job != null &&
      !bulkCancelTerminal(current.job.status);

    if (keepRunning) {
      // Hide the popup; remember the job id so reopen can fetch once (no background poll).
      setBulkCancelDialog({ ...current, open: false });
      return;
    }

    setBulkCancelDialog(emptyBulkCancelDialog());
    if (jobFinished) {
      void refreshAfterCancelRef.current();
    }
  };

  const openBulkCancelPopup = async () => {
    // Prefer the job this tab is already watching (popup closed mid-run).
    const localWatchId = bulkCancelDialog.watchJobId;
    if (localWatchId) {
      try {
        const watched = await shipmentStore.getBulkCancelJob(localWatchId);
        resumeBulkCancelJob(watched);
        return;
      } catch {
        // Fall through to the active-job API.
      }
    }

    try {
      const active = await shipmentStore.getActiveBulkCancelJob();
      if (active) {
        resumeBulkCancelJob(active);
        return;
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Couldn't check bulk cancel.";
      showError("Bulk cancel", message);
      return;
    }

    const selectedIds =
      allSelected || selectedShipmentIdsMap.size === 0
        ? []
        : Array.from(selectedShipmentIdsMap.keys());

    const shipmentsById = new Map(
      (shipmentStore.shipments || []).map((shipment) => [
        shipment.id,
        shipment,
      ]),
    );
    const isPastCancellableStatus = (status: number | undefined) => {
      const value = Number(status);
      return !Number.isFinite(value) || value >= ShipmentStatusEnum.DELIVERED;
    };
    const skippedCount = selectedIds.filter((id) => {
      const shipment = shipmentsById.get(id);
      if (!shipment) return false;
      return isPastCancellableStatus(shipment.status);
    }).length;
    const shipmentIds = selectedIds.filter((id) => {
      const shipment = shipmentsById.get(id);
      if (!shipment) return true;
      return !isPastCancellableStatus(shipment.status);
    });
    const filtersApplied = hasAppliedCancelFilter();

    if (shipmentIds.length === 0 && !filtersApplied) {
      showError(
        "Nothing to cancel",
        selectedIds.length > 0
          ? "Selected shipments can't be cancelled."
            : "Select shipments, or filter by a customer or an order.",
      );
      return;
    }

    if (shipmentIds.length > 0) {
      setBulkCancelDialog({
        ...emptyBulkCancelDialog(),
        open: true,
        scope: "selected",
        shipmentIds,
        skippedCount,
      });
      return;
    }

    const filterCount = Number(shipmentStore.totalCount) || 0;
    if (filterCount <= 0) {
      showError("Nothing to cancel", "No shipments match these filters.");
      return;
    }

    setBulkCancelDialog({
      ...emptyBulkCancelDialog(),
      open: true,
      scope: "filters",
      filterCount,
    });
  };

  const handleBulkCancelConfirm = async (reason: string) => {
    const scope = bulkCancelDialog.scope;
    const shipmentIds = bulkCancelDialog.shipmentIds;
    setBulkCancelDialog((current) => ({
      ...current,
      submitting: true,
      error: null,
      reason,
    }));
    try {
      const result = await shipmentStore.createBulkCancelJob({
        reason,
        scope,
        shipmentIds,
        filters:
          scope === "filters"
            ? shipmentStore.bulkCancelFilters(withLockedFilters(params))
            : undefined,
      });
      const job = result.conflict
        ? await shipmentStore.getBulkCancelJob(result.jobId)
        : result.job;
      const finished = bulkCancelTerminal(job.status);
      setBulkCancelDialog((current) => ({
        ...current,
        open: true,
        submitting: false,
        error: null,
        job,
        scope: job.scope,
        watchJobId: finished ? null : job.jobId,
      }));
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Couldn't start the bulk cancel.";
      setBulkCancelDialog((current) => ({
        ...current,
        submitting: false,
        error: message,
      }));
    }
  };

  useEffect(() => {
    const watchJobId = bulkCancelDialog.watchJobId;
    if (!bulkCancelDialog.open || !watchJobId) return;

    let stopped = false;
    const tick = async () => {
      try {
        const job = await shipmentStore.getBulkCancelJob(watchJobId);
        if (stopped) return;
        const finished = bulkCancelTerminal(job.status);
        setBulkCancelDialog((current) => {
          if (!current.open || current.watchJobId !== watchJobId) {
            return current;
          }
          // Listing refresh waits until the admin clicks Close.
          return {
            ...current,
            job,
            scope: job.scope,
            watchJobId: finished ? null : current.watchJobId,
          };
        });
      } catch {
        // Keep the last counts. The next poll tries again.
      }
    };

    void tick();
    const timer = window.setInterval(tick, 2000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [bulkCancelDialog.open, bulkCancelDialog.watchJobId, shipmentStore]);

  const hasAppliedRemovePacksFilter = () => {
    const hasValue = (item: unknown) => {
      if (item == null) return false;
      if (typeof item === "string") return item.trim() !== "";
      if (typeof item === "number") return Number.isFinite(item);
      if (typeof item === "object" && "id" in item) {
        return String((item as { id?: unknown }).id ?? "").trim() !== "";
      }
      return false;
    };
    const hasItems = (value: readonly unknown[] | null | undefined) =>
      (value ?? []).some(hasValue);
    return hasItems(params.order_id) || hasItems(params.c_id);
  };

  const resumeBulkRemovePacksJob = (job: BulkRemovePacksJobState) => {
    const stillRunning = !bulkRemovePacksTerminal(job.status);
    setBulkRemovePacksDialog({
      ...emptyBulkRemovePacksDialog(),
      open: true,
      scope: job.scope,
      job,
      watchJobId: stillRunning ? job.jobId : null,
    });
  };

  const closeBulkRemovePacksPopup = () => {
    const current = bulkRemovePacksDialog;
    if (current.submitting) return;
    const jobFinished =
      current.job != null && bulkRemovePacksTerminal(current.job.status);
    const jobRunning =
      current.job != null && !bulkRemovePacksTerminal(current.job.status);

    if (jobRunning) {
      setBulkRemovePacksDialog({ ...current, open: false });
      return;
    }

    setBulkRemovePacksDialog(emptyBulkRemovePacksDialog());
    if (jobFinished) {
      void refreshAfterRemovePacksRef.current();
    }
  };

  const openBulkRemovePacksPopup = async () => {
    const localWatchId = bulkRemovePacksDialog.watchJobId;
    if (localWatchId) {
      try {
        const watched = await shipmentStore.getBulkRemovePacksJob(localWatchId);
        resumeBulkRemovePacksJob(watched);
        return;
      } catch {
        // Fall through to the active-job API.
      }
    }

    try {
      const active = await shipmentStore.getActiveBulkRemovePacksJob();
      if (active) {
        resumeBulkRemovePacksJob(active);
        return;
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Couldn't check bulk remove packs.";
      showError("Bulk remove packs", message);
      return;
    }

    const selectedIds =
      allSelected || selectedShipmentIdsMap.size === 0
        ? []
        : Array.from(selectedShipmentIdsMap.keys());
    const filtersApplied = hasAppliedRemovePacksFilter();

    if (selectedIds.length === 0 && !filtersApplied) {
      showError(
        "Nothing to remove",
        allSelected
          ? "Select-all needs a customer or order filter."
          : "Select shipments, or filter by a customer or an order.",
      );
      return;
    }

    if (selectedIds.length > 0) {
      setBulkRemovePacksDialog({
        ...emptyBulkRemovePacksDialog(),
        open: true,
        scope: "selected",
        shipmentIds: selectedIds,
      });
      return;
    }

    const filterCount = Number(shipmentStore.totalCount) || 0;
    if (filterCount <= 0) {
      showError(
        "Nothing to remove",
        "No shipments match these filters.",
      );
      return;
    }

    setBulkRemovePacksDialog({
      ...emptyBulkRemovePacksDialog(),
      open: true,
      scope: "filters",
      filterCount,
    });
  };

  const handleBulkRemovePacksConfirm = async () => {
    const scope = bulkRemovePacksDialog.scope;
    const shipmentIds = bulkRemovePacksDialog.shipmentIds;
    setBulkRemovePacksDialog((current) => ({
      ...current,
      submitting: true,
      error: null,
    }));
    try {
      const result = await shipmentStore.createBulkRemovePacksJob({
        scope,
        shipmentIds,
        filters:
          scope === "filters"
            ? shipmentStore.bulkRemovePacksFilters(withLockedFilters(params))
            : undefined,
      });
      const job = result.conflict
        ? await shipmentStore.getBulkRemovePacksJob(result.jobId)
        : result.job;
      const finished = bulkRemovePacksTerminal(job.status);
      setBulkRemovePacksDialog((current) => ({
        ...current,
        open: true,
        submitting: false,
        error: null,
        job,
        scope: job.scope,
        watchJobId: finished ? null : job.jobId,
      }));
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Couldn't start bulk remove packs.";
      setBulkRemovePacksDialog((current) => ({
        ...current,
        submitting: false,
        error: message,
      }));
    }
  };

  useEffect(() => {
    refreshAfterRemovePacksRef.current = async () => {
      setSelectedShipmentIdsMap(new Map());
      setAllSelected(false);
      setClearShipments(true);
      await fetchShipments();
    };
  }, [fetchShipments]);

  useEffect(() => {
    const watchJobId = bulkRemovePacksDialog.watchJobId;
    if (!bulkRemovePacksDialog.open || !watchJobId) return;

    let stopped = false;
    const tick = async () => {
      try {
        const job = await shipmentStore.getBulkRemovePacksJob(watchJobId);
        if (stopped) return;
        const finished = bulkRemovePacksTerminal(job.status);
        setBulkRemovePacksDialog((current) => {
          if (!current.open || current.watchJobId !== watchJobId) {
            return current;
          }
          return {
            ...current,
            job,
            scope: job.scope,
            watchJobId: finished ? null : current.watchJobId,
          };
        });
      } catch {
        // Keep the last counts. The next poll tries again.
      }
    };

    void tick();
    const timer = window.setInterval(tick, 2000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [bulkRemovePacksDialog.open, bulkRemovePacksDialog.watchJobId, shipmentStore]);

  const handleBulkActions = (action: string | null) => {
    handleClose();
    if (action === "addPacks") {
      openAddPacksPopup(selectedShipmentIdsMap);
    } else if (action === "bulkEditShipments") {
      setActivePopup("bulkEditShipments");
    } else if (action === "bulkShippingCost") {
      openBulkShippingCostPopup();
    } else if (action === "bulkCancel") {
      openBulkCancelPopup();
    } else if (action === "bulkRemovePacks") {
      void openBulkRemovePacksPopup();
    } else {
      setActivePopup(action);
    }
  };

  const handleClosePopup = () => {
    if (activePopup === "addPacks") {
      if (selectedShipmentIdsMap.size === 1) {
        setSelectedShipmentIdsMap(new Map());
      }
      setAddPacksParams(null);
    }
    if (activePopup === "bulkEditShipments") {
      resetBulkEditFields();
    }
    if (activePopup === "bulkShippingCost") {
      resetBulkShippingCost();
    }
    setActivePopup(null);
  };

  const handleSavePacks = async (payload: {
    packs: AddShipmentPackLineRequest[];
    items: AddShipmentItemLineRequest[];
  }) => {
    const selectedIds = Array.from(selectedShipmentIdsMap.keys());

    const res = await shipmentStore.addShipmentLines(
      selectedIds,
      payload.packs,
      payload.items,
    );
    if (res?.success) {
      setClearShipments(true);
      setAddPacksParams(null);
      setActivePopup(null);
      setParams((prevParams) => ({
        ...prevParams,
        page: 1,
      }));
    } else {
      showError(
        "Error Occured",
        res?.message || shipmentStore.error || "Error adding lines",
      );
    }
  };

  // const handleShipmentsDownloadTemplate = async (filename: string) => {
  //   const url =
  //     "https://procureasystorage.s3.ap-south-1.amazonaws.com/templates/CSV_Shipping_Template.csv";

  //   try {
  //     try {
  //       const response = await fetch(url);
  //       if (!response.ok) {
  //         throw new Error(`HTTP error! status: ${response.status}`);
  //       }

  //       const blob = await response.blob();
  //       const downloadUrl = window.URL.createObjectURL(blob);

  //       const a = document.createElement("a");
  //       a.href = downloadUrl;
  //       a.download = filename || "CSV_Shipping_Template.csv";
  //       a.style.display = "none";
  //       document.body.appendChild(a);
  //       a.click();
  //       document.body.removeChild(a);

  //       window.URL.revokeObjectURL(downloadUrl);
  //     } catch (fetchError) {
  //       console.log("Fetch failed, trying window.open approach:", fetchError);
  //       window.open(url, "_blank");
  //     }
  //   } catch (error: any) {
  //     console.error("Download error:", error);
  //     showError(
  //       "Error",
  //       error?.message || "Failed to download CSV_Shipping_Template",
  //     );
  //   }
  // };

  return (
    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
      {userStore.hasPermission(viewPermission) ? (
        <Paper
          elevation={compactLayout ? 0 : 1}
          sx={
            compactLayout
              ? { boxShadow: "none", background: "transparent" }
              : undefined
          }
        >
          <Box
            sx={{
              marginTop: compactLayout ? 0 : "0.6rem",
              paddingX: compactLayout ? 0 : 4,
              paddingY: compactLayout ? 1 : 2,
              boxShadow: compactLayout
                ? "none"
                : "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
              borderRadius: "4px",
              minHeight: compactLayout
                ? undefined
                : { xs: "60vh", md: window.innerHeight - 110 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent:
                  pageHeaderStart || !hidePageHeader
                    ? "space-between"
                    : "flex-end",
                alignItems: { xs: "flex-start", md: "center" },
                gap: 2,
              }}
            >
              {pageHeaderStart ? (
                <Box>{pageHeaderStart}</Box>
              ) : (
                !hidePageHeader && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      gap: 0.5,
                    }}
                  >
                    <CustomTypography variant="heading3">
                      Shipments
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      color={theme.palette.customColors?.greyTextColor}
                    >
                      Manage shipments, apply bulk actions, and export shipment
                      data.
                    </CustomTypography>
                  </Box>
                )
              )}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: { xs: "flex-start", md: "flex-end" },
                  gap: 2,
                }}
              >
                {userStore.hasPermission(EmployeeRoles.CREATE_SHIPMENTS) && (
                  <Box>
                    <AppButton
                      variant="primary"
                      label="Add Shipments"
                      onClick={() => setIsCreateShipmentWizardOpen(true)}
                    />
                  </Box>
                )}
                {userStore.hasPermission(EmployeeRoles.EDIT_SHIPMENTS) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      gap: 1,
                    }}
                  >
                    <Box ref={actionsAnchorRef}>
                      <AppButton
                        variant="grayscale"
                        label={`Actions${
                          selectedShipmentIdsMap.size > 0 || allSelected
                            ? ` (${
                                allSelected
                                  ? shipmentStore.totalCount
                                  : selectedShipmentIdsMap.size
                              })`
                            : ""
                        }`}
                        onClick={handleBulkMenu}
                        customStyles={{ minWidth: 180 }}
                      />
                    </Box>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                      disableAutoFocusItem
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 1,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow:
                              "0px 20px 45px rgba(15, 23, 42, 0.15), 0px 6px 18px rgba(15, 23, 42, 0.08)",
                            minWidth: 240,
                            overflow: "hidden",
                          },
                        },
                      }}
                    >
                      <>
                        <MenuItem
                          onClick={() => handleBulkActions("bulkEditShipments")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Update Dimensions, Weight & Partner
                          </CustomTypography>
                        </MenuItem>
                        {canBulkUpdateShippingCost && (
                          <MenuItem
                            onClick={() =>
                              handleBulkActions("bulkShippingCost")
                            }
                            sx={{
                              fontSize: "0.9rem",
                              px: 2,
                              py: 1.25,
                              gap: 1,
                              borderTop: "1px solid " + theme.palette.divider,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors?.lightPurple ||
                                  theme.palette.action.hover,
                              },
                            }}
                          >
                            <CustomTypography variant="body2" fontWeight={600}>
                              Bulk Update Shipping Cost
                            </CustomTypography>
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => handleBulkActions("addStatus")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Add Status
                          </CustomTypography>
                        </MenuItem>
                        {userStore.hasPermission(
                          EmployeeRoles.CANCEL_USER_PLACED_SHIPMENTS,
                        ) && (
                          <MenuItem
                            onClick={() => handleBulkActions("bulkCancel")}
                            sx={{
                              fontSize: "0.9rem",
                              px: 2,
                              py: 1.25,
                              gap: 1,
                              borderTop: "1px solid " + theme.palette.divider,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors?.lightPurple ||
                                  theme.palette.action.hover,
                              },
                            }}
                          >
                            <CustomTypography
                              variant="body2"
                              fontWeight={600}
                              color={theme.palette.customColors?.errorRed}
                            >
                              Bulk Cancel Shipments
                            </CustomTypography>
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => handleBulkActions("bulkRemovePacks")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            color:
                              theme.palette.customColors?.errorRed ||
                              theme.palette.error.main,
                            "&:hover": {
                              backgroundColor: `${theme.palette.customColors?.errorRed || theme.palette.error.main}14`,
                            },
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            fontWeight={600}
                            color={
                              theme.palette.customColors?.errorRed ||
                              theme.palette.error.main
                            }
                          >
                            Bulk Remove Packs
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleBulkActions("addPacks")}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Add Pack
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={handleOpenLinesCsvDialog}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Bulk Upload Packs / Items (CSV)
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={() => handleDialogOpen()}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Add Tracking Details
                          </CustomTypography>
                        </MenuItem>
                        <MenuItem
                          onClick={handleOpenBookingSyncConfirm}
                          disabled={isBookingSyncing}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            {isBookingSyncing
                              ? "Syncing Booking…"
                              : "Sync Booking"}
                          </CustomTypography>
                        </MenuItem>
                      </>

                      {userStore.hasPermission(
                        EmployeeRoles.EDIT_SHIPMENTS,
                        true,
                      ) && (
                        <MenuItem
                          onClick={downloadCsv}
                          sx={{
                            fontSize: "0.9rem",
                            px: 2,
                            py: 1.25,
                            gap: 1,
                            borderTop: "1px solid " + theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.customColors?.lightPurple ||
                                theme.palette.action.hover,
                            },
                          }}
                        >
                          <CustomTypography variant="body2" fontWeight={600}>
                            Download Filtered Data CSV
                          </CustomTypography>
                        </MenuItem>
                      )}
                      {activeStatusTab !== "new" &&
                        userStore.hasPermission(
                          EmployeeRoles.EDIT_SHIPMENTS,
                          true,
                        ) && (
                          <MenuItem
                            onClick={downloadShiprocketCsv}
                            sx={{
                              fontSize: "0.9rem",
                              px: 2,
                              py: 1.25,
                              gap: 1,
                              borderTop: "1px solid " + theme.palette.divider,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors?.lightPurple ||
                                  theme.palette.action.hover,
                              },
                            }}
                          >
                            <CustomTypography variant="body2" fontWeight={600}>
                              Download Shiprocket CSV
                            </CustomTypography>
                          </MenuItem>
                        )}
                      {activeStatusTab !== "new" &&
                        userStore.hasPermission(
                          EmployeeRoles.EDIT_SHIPMENTS,
                          true,
                        ) && (
                          <MenuItem
                            onClick={downloadShipmozoCsv}
                            sx={{
                              fontSize: "0.9rem",
                              px: 2,
                              py: 1.25,
                              gap: 1,
                              borderTop: "1px solid " + theme.palette.divider,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors?.lightPurple ||
                                  theme.palette.action.hover,
                              },
                            }}
                          >
                            <CustomTypography variant="body2" fontWeight={600}>
                              Download Shipmozo Excel
                            </CustomTypography>
                          </MenuItem>
                        )}
                    </Menu>
                  </Box>
                )}
                {headerTrailingActions}
              </Box>
            </Box>

            {pageHeaderStart ? <Divider sx={{ mt: 2, mb: 3 }} /> : null}

            {hasRunningLinesUploadJobs && (
              <Box sx={{ mt: 2, width: "100%" }}>
                <JobRecordsTable
                  maxHeight={240}
                  data={shipmentStore.runningLinesUploadJobs}
                  totalCount={shipmentStore.runningLinesUploadJobs.length}
                  onRefreshClick={() => {
                    void shipmentStore.fetchRunningLinesUploadJobs();
                  }}
                />
                <InlineFormError
                  message={shipmentStore.runningLinesUploadJobsError ?? ""}
                  sx={{ mt: 1 }}
                />
              </Box>
            )}

            {simpleSearchOnly ? (
              <FilterComponent
                onFilterChange={handleFilterChange}
                deliveryPartners={shipmentStore?.deliveryPartners}
                lockedOrderId={lockedOrderId}
                lockedInventoryType={lockedInventoryType}
                simpleSearchOnly
              />
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  borderColor: "divider",
                  overflow: "hidden",
                }}
              >
                <Box
                  onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 0.5,
                    cursor: "pointer",
                    borderBottom: isFiltersExpanded ? "1px solid" : "none",
                    borderColor: "divider",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <CustomTypography variant="heading7">
                    Search & Filters
                  </CustomTypography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsFiltersExpanded(!isFiltersExpanded);
                    }}
                  >
                    {isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>
                <Collapse in={isFiltersExpanded} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      p: 2,
                    }}
                  >
                    <FilterComponent
                      onFilterChange={handleFilterChange}
                      deliveryPartners={shipmentStore?.deliveryPartners}
                      lockedOrderId={lockedOrderId}
                      lockedInventoryType={lockedInventoryType}
                    />
                  </Box>
                </Collapse>
              </Paper>
            )}

            {shipmentStore.countByStatus?.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 1,
                  py: 0.75,
                  px: 1,
                  fontSize: "0.75rem",
                }}
              >
                <CustomTypography variant="body2" fontWeight={500}>
                  Today's counts (updated every 5 min):{" "}
                </CustomTypography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {shipmentStore.countByStatus.map((item, idx) => {
                    return (
                      <React.Fragment key={item.tab_id}>
                        {idx > 0 && (
                          <Box
                            component="span"
                            sx={{ color: theme.palette.divider, mx: 0.25 }}
                          >
                            ·
                          </Box>
                        )}
                        <Box sx={{ display: "inline-flex" }}>
                          <CustomTypography variant="body3" fontWeight={500}>
                            {item.label.toUpperCase()}:{" "}
                          </CustomTypography>
                          <CustomTypography variant="body1" fontWeight={600}>
                            {item.requests_count}
                          </CustomTypography>
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>
              </Box>
            )}

            <Tabs
              value={activeStatusTab}
              onChange={handleStatusTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={(t) => ({ ...listingStatusTabsStyles(t), mt: 1 })}
            >
              {SHIPMENT_STATUS_TABS.map((tab) => (
                <Tab
                  key={tab.id}
                  label={tab.label.toUpperCase()}
                  id={`shipment-tab-${tab.id}`}
                  value={tab.id}
                />
              ))}
            </Tabs>

            {shipmentStore.loading && editShipmentId == null ? (
              <Box
                sx={{
                  minHeight: "40vh",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* <Box sx={{ display: "flex", mt: 2 }}>
                  <Checkbox
                    color="primary"
                    indeterminate={false}
                    checked={allSelected}
                    onClick={() => {
                      toggleAllSelected(!allSelected);
                      setSelectedShipmentIdsMap(new Map());
                    }}
                    sx={{ padding: 0, marginLeft: "24px", marginRight: "10px" }}
                  />
                  <CustomTypography variant="heading7">
                    {allSelected
                      ? shipmentStore.totalCount + " selected."
                      : shipmentStore.totalCount}
                  </CustomTypography>
                </Box> */}
                <ShipmentsTable
                  list={shipmentStore.shipments || []}
                  limit={params.limit}
                  page_no={params.page}
                  total_count={shipmentStore.totalCount}
                  total_pages={shipmentStore.totalPages}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  onDelete={(id) => {
                    setDeleteId(id);
                  }}
                  onActivityHistory={(id) => {
                    shipmentStore.openActivityLogs(id);
                  }}
                  onReturned={(id) => {
                    setReturnedId(id);
                  }}
                  onRemovePacks={(id) => {
                    setRemovePacksFromShipmentId(id);
                  }}
                  onAddPacks={handleAddPacksForShipment}
                  allSelected={allSelected}
                  // onSelectedShipmentsChange={setSelectedShipmentIds}
                  clearShipments={clearShipments}
                  onSortChange={handleSortChange}
                  orderBy={params.sortBy}
                  orderDirection={params.sortType}
                  selectedShipmentIdsMap={selectedShipmentIdsMap}
                  selectAllItem={() => {
                    const map = new Map<number, number>();
                    shipmentStore.shipments?.forEach((item) =>
                      map.set(item.id, item.id),
                    );
                    toggleAllSelected(false);
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectAllItem={() => {
                    toggleAllSelected(false);
                    setSelectedShipmentIdsMap(new Map());
                  }}
                  selectItem={(item: any) => {
                    toggleAllSelected(false);

                    const map = new Map(selectedShipmentIdsMap);
                    map.set(item.id, item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  deSelectItem={(item: any) => {
                    toggleAllSelected(false);

                    const map = new Map(selectedShipmentIdsMap);
                    map.delete(item.id);
                    setSelectedShipmentIdsMap(map);
                  }}
                  updateShipmentStatus={(id, status) =>
                    handleUpdateShipmentStatus(id, status)
                  }
                  onRefresh={handleRefreshShipments}
                  refreshing={shipmentStore.loading}
                  onEditShipment={(shipmentId) => {
                    try {
                      setEditShipmentId(shipmentId);
                    } catch {
                      // Keep the current edit target if opening fails.
                    }
                  }}
                />
                {deleteId !== null && (
                  <CancelShipmentPopup
                    open={true}
                    shipmentId={deleteId}
                    onConfirm={handleCancelShipmentConfirm}
                    onCancel={() => setDeleteId(null)}
                  />
                )}
                <BulkCancelShipmentPopup
                  open={bulkCancelDialog.open}
                  scope={bulkCancelDialog.scope}
                  count={
                    bulkCancelDialog.scope === "selected"
                      ? bulkCancelDialog.shipmentIds.length
                      : bulkCancelDialog.filterCount
                  }
                  submitting={bulkCancelDialog.submitting}
                  error={bulkCancelDialog.error}
                  job={bulkCancelDialog.job}
                  onConfirm={handleBulkCancelConfirm}
                  onClose={closeBulkCancelPopup}
                />

                {returnedId !== null && (
                  <DeleteConfirmation
                    id={returnedId}
                    open={true}
                    onDeleteConfirm={() => handleReturnedConfirm(returnedId)}
                    onCancel={() => setReturnedId(null)}
                    title="Confirm"
                    content="Are you sure you want to mark this shipment as RETURNED?"
                  />
                )}

                {removePacksFromShipmentId !== null && (
                  <RemovePacksConfirmationPopup
                    id={removePacksFromShipmentId}
                    open={true}
                    onRemovePacksConfirm={() =>
                      handleRemovePacksConfirm(removePacksFromShipmentId)
                    }
                    onCancel={() => setRemovePacksFromShipmentId(null)}
                    title="Confirm remove"
                    content="Are you sure you want to remove packs from this shipment?"
                  />
                )}

                <BulkRemovePacksPopup
                  open={bulkRemovePacksDialog.open}
                  scope={bulkRemovePacksDialog.scope}
                  count={
                    bulkRemovePacksDialog.scope === "selected"
                      ? bulkRemovePacksDialog.shipmentIds.length
                      : bulkRemovePacksDialog.filterCount
                  }
                  submitting={bulkRemovePacksDialog.submitting}
                  error={bulkRemovePacksDialog.error}
                  job={bulkRemovePacksDialog.job}
                  onConfirm={() => {
                    void handleBulkRemovePacksConfirm();
                  }}
                  onClose={closeBulkRemovePacksPopup}
                />

                <Dialog
                  open={isBookingSyncConfirmOpen}
                  onClose={() => {
                    if (!isBookingSyncing) {
                      setIsBookingSyncConfirmOpen(false);
                    }
                  }}
                  maxWidth="sm"
                  fullWidth
                >
                  <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Grid2 container spacing={1.5}>
                      <Grid2 size={12}>
                        <CustomTypography
                          variant="heading6"
                          color={theme.palette.customColors.blackText}
                        >
                          Sync Booking
                        </CustomTypography>
                      </Grid2>
                      <Grid2 size={12}>
                        <CustomTypography
                          variant="body2"
                          color={theme.palette.customColors.greyTextColor}
                        >
                          This will queue all eligible Ready-to-Ship shipments
                          from the last 6 months for carrier booking. Ineligible
                          rows will be skipped.
                        </CustomTypography>
                      </Grid2>
                    </Grid2>
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <AppButton
                      label="Cancel"
                      variant="secondary"
                      disabled={isBookingSyncing}
                      onClick={() => setIsBookingSyncConfirmOpen(false)}
                    />
                    <AppButton
                      label={isBookingSyncing ? "Queuing…" : "Queue Booking"}
                      variant="primary"
                      disabled={isBookingSyncing}
                      onClick={handleSyncBooking}
                    />
                  </DialogActions>
                </Dialog>

                <Dialog
                  open={activePopup === "bulkShippingCost"}
                  onClose={
                    bulkShippingCostDialog.submitting
                      ? undefined
                      : handleClosePopup
                  }
                  maxWidth="xs"
                  fullWidth
                >
                  <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <Grid2 container spacing={1.5}>
                      <Grid2 size={12}>
                        <CustomTypography
                          variant="heading6"
                          color={theme.palette.customColors.blackText}
                        >
                          Bulk Update Shipping Cost
                        </CustomTypography>
                      </Grid2>
                      <Grid2 size={12}>
                        <CustomTypography
                          variant="body2"
                          color={theme.palette.customColors.greyTextColor}
                        >
                          Applies to {selectedShipmentIdsMap.size} selected
                          shipment(s). Only New and Ready to Ship shipments are
                          updated.
                        </CustomTypography>
                      </Grid2>
                      {bulkShippingCostDialog.error && (
                        <Grid2 size={12}>
                          <Alert
                            severity="error"
                            onClose={() =>
                              setBulkShippingCostDialog((current) => ({
                                ...current,
                                error: null,
                              }))
                            }
                          >
                            {bulkShippingCostDialog.error}
                          </Alert>
                        </Grid2>
                      )}
                      <Grid2 size={12}>
                        <CustomTypography
                          variant="body2"
                          sx={{ mb: 0.75, fontWeight: 500 }}
                        >
                          Shipping Cost (₹)
                        </CustomTypography>
                        <NumericTextField
                          fullWidth
                          size="small"
                          placeholder="e.g. 150.50"
                          value={shipmentStore.bulkShippingCostDraft}
                          disabled={bulkShippingCostDialog.submitting}
                          onChange={(event) => {
                            shipmentStore.setBulkShippingCostDraft(
                              event.target.value,
                            );
                            setBulkShippingCostDialog((current) =>
                              current.error
                                ? { ...current, error: null }
                                : current,
                            );
                          }}
                          sx={textFieldSx}
                        />
                      </Grid2>
                    </Grid2>
                  </DialogContent>
                  <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <AppButton
                      label="Cancel"
                      variant="secondary"
                      disabled={bulkShippingCostDialog.submitting}
                      onClick={handleClosePopup}
                    />
                    <AppButton
                      label={
                        bulkShippingCostDialog.submitting ? "Saving…" : "Save"
                      }
                      variant="primary"
                      disabled={bulkShippingCostDialog.submitting}
                      onClick={handleBulkShippingCostSubmit}
                    />
                  </DialogActions>
                </Dialog>

                {activePopup === "bulkEditShipments" && (
                  <Dialog
                    open={true}
                    onClose={bulkEditSubmitting ? undefined : handleClosePopup}
                    maxWidth="md"
                    fullWidth
                    slotProps={{
                      transition: {
                        timeout: 300,
                        sx: {
                          backgroundColor: theme.palette.customColors?.backdrop,
                          backdropFilter: "blur(4px)",
                        },
                      },
                      backdrop: {
                        timeout: 300,
                        sx: {
                          backgroundColor: theme.palette.customColors?.backdrop,
                          backdropFilter: "blur(4px)",
                        },
                      },
                      paper: {
                        sx: {
                          borderRadius: "16px",
                          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
                          overflow: "hidden",
                          maxHeight: "90vh",
                          background: `linear-gradient(135deg, ${theme.palette.customColors?.white || "#fff"} 0%, ${theme.palette.customColors?.lightGreyColor || "#f5f5f5"} 100%)`,
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: { xs: "12px 16px 12px", sm: "16px 20px 16px" },
                        borderBottom: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
                      }}
                    >
                      <CustomTypography variant="heading4">
                        Bulk Update Shipment Fields
                      </CustomTypography>
                      <IconButton
                        onClick={handleClosePopup}
                        disabled={bulkEditSubmitting}
                        sx={{
                          color:
                            theme.palette.customColors?.greyTextColor || "#666",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.lightGreyColor,
                            color: theme.palette.customColors?.blackBorder,
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <DialogContent
                      sx={{
                        padding: { xs: "20px", sm: "24px" },
                        "&::-webkit-scrollbar": { width: "8px" },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor:
                            theme.palette.customColors?.greyBackground,
                          borderRadius: "4px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor:
                            theme.palette.customColors?.greyDarkborder,
                          borderRadius: "4px",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.greyTextColor,
                          },
                        },
                      }}
                    >
                      <Box sx={{ mb: 3 }}>
                        <CustomTypography
                          variant="heading6"
                          sx={{ fontWeight: 600 }}
                          style={{ marginBottom: "8px" }}
                        >
                          Add boxes
                        </CustomTypography>
                        <CustomTypography
                          variant="body3"
                          style={{ marginBottom: "16px" }}
                        >
                          Adds these boxes to each selected shipment. Existing
                          boxes are not replaced or removed. Empty rows are
                          skipped.
                        </CustomTypography>
                        {bulkEditInlineMessage && (
                          <Alert
                            severity={bulkEditInlineMessage.severity}
                            onClose={() => setBulkEditInlineMessage(null)}
                            sx={{ mb: 2 }}
                          >
                            {bulkEditInlineMessage.text}
                          </Alert>
                        )}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {bulkBoxDrafts.map((draft, index) => (
                            <Box
                              key={draft.key}
                              sx={{
                                p: 1.5,
                                borderRadius: "8px",
                                border: `1px solid ${
                                  theme.palette.customColors?.greyBorder ||
                                  "#e0e0e0"
                                }`,
                                backgroundColor:
                                  theme.palette.customColors?.white || "#fff",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  mb: 1.25,
                                }}
                              >
                                <CustomTypography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Box {index + 1}
                                </CustomTypography>
                                {bulkBoxDrafts.length > 1 && (
                                  <IconButton
                                    size="small"
                                    aria-label={`Remove draft box ${index + 1}`}
                                    onClick={() =>
                                      removeBulkBoxDraft(draft.key)
                                    }
                                    disabled={bulkEditSubmitting}
                                    sx={{
                                      color:
                                        theme.palette.customColors
                                          ?.greyTextColor || "#666",
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.customColors
                                            ?.lightGreyColor,
                                        color:
                                          theme.palette.customColors
                                            ?.blackBorder,
                                      },
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                              <Grid2
                                container
                                spacing={2}
                                columns={{ xs: 12, sm: 12, md: 12 }}
                              >
                                {BULK_BOX_DIMENSION_FIELDS.map(
                                  ({ field, label }) => (
                                    <Grid2
                                      key={field}
                                      size={{ xs: 12, sm: 6, md: 3 }}
                                    >
                                      <CustomTypography
                                        variant="body2"
                                        sx={{ mb: 0.75, fontWeight: 500 }}
                                      >
                                        {label}
                                      </CustomTypography>
                                      <NumericTextField
                                        size="small"
                                        value={draft[field]}
                                        onChange={(e) =>
                                          updateBulkBoxDraft(
                                            draft.key,
                                            field,
                                            e.target.value,
                                          )
                                        }
                                        sx={textFieldSx}
                                        fullWidth
                                        disabled={bulkEditSubmitting}
                                      />
                                    </Grid2>
                                  ),
                                )}
                              </Grid2>
                            </Box>
                          ))}
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <AppButton
                            label="Add box"
                            variant="secondary"
                            startIcon={<AddIcon />}
                            disabled={bulkEditSubmitting}
                            onClick={addBulkBoxDraft}
                            customStyles={{ minHeight: 36, px: 1.5 }}
                            fontSize="0.75rem"
                          />
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          my: 3,
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      />

                      <Box sx={{ mb: 1 }}>
                        <CustomTypography
                          variant="heading6"
                          sx={{ fontWeight: 600 }}
                          style={{ marginBottom: "16px" }}
                        >
                          Partner Details
                        </CustomTypography>
                        <Grid2
                          container
                          spacing={2}
                          columns={{ xs: 12, sm: 12, md: 12 }}
                        >
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <CustomTypography
                              variant="body2"
                              sx={{ mb: 0.75, fontWeight: 500 }}
                            >
                              Shipment Method
                            </CustomTypography>
                            <AppSearchDropdownMulti
                              isMultiSelect={false}
                              title="Select Shipment Method"
                              local
                              list={SHIPMENT_METHOD_OPTIONS.map((method) => ({
                                id: String(method),
                                value: method,
                              }))}
                              value={
                                bulkEditFields.shipment_method
                                  ? [
                                      {
                                        id: String(
                                          bulkEditFields.shipment_method,
                                        ),
                                        value: bulkEditFields.shipment_method,
                                      },
                                    ]
                                  : []
                              }
                              handleSelect={(selectedItems) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  shipment_method: (selectedItems[0]?.value ??
                                    "") as ShipmentMethod | "",
                                }))
                              }
                              borderColor={
                                theme.palette.customColors?.greyBorder || "#ddd"
                              }
                              inputHeight="40px"
                              borderRadius="4px"
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <CustomTypography
                              variant="body2"
                              sx={{ mb: 0.75, fontWeight: 500 }}
                            >
                              Booking Type
                            </CustomTypography>
                            <AppSearchDropdownMulti
                              isMultiSelect={false}
                              title="Select Booking Type"
                              local
                              list={SHIPMENT_BOOKING_TYPE_OPTIONS.map(
                                (option) => ({
                                  id: option.id,
                                  value: option.value,
                                }),
                              )}
                              value={
                                bulkEditFields.booking_type
                                  ? [
                                      {
                                        id: bulkEditFields.booking_type,
                                        value:
                                          SHIPMENT_BOOKING_TYPE_OPTIONS.find(
                                            (option) =>
                                              option.id ===
                                              bulkEditFields.booking_type,
                                          )?.value ||
                                          bulkEditFields.booking_type,
                                      },
                                    ]
                                  : []
                              }
                              handleSelect={(selectedItems) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  booking_type: (selectedItems[0]?.id ?? "") as
                                    | ShipmentBookingType
                                    | "",
                                }))
                              }
                              borderColor={
                                theme.palette.customColors?.greyBorder || "#ddd"
                              }
                              inputHeight="40px"
                              borderRadius="4px"
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <CustomTypography
                              variant="body2"
                              sx={{ mb: 0.75, fontWeight: 500 }}
                            >
                              Delivery Partner
                            </CustomTypography>
                            <CarriersSearchDropdown
                              enabled={activePopup === "bulkEditShipments"}
                              title="Delivery Partner"
                              value={
                                bulkEditFields.delivery_partner_id !== -1
                                  ? [
                                      {
                                        id: String(
                                          bulkEditFields.delivery_partner_id,
                                        ),
                                        value:
                                          bulkEditFields.delivery_partner_name,
                                      },
                                    ]
                                  : []
                              }
                              handleSelect={(selectedItems) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  delivery_partner_id: Number(
                                    selectedItems[0]?.id ?? -1,
                                  ),
                                  delivery_partner_name:
                                    selectedItems[0]?.value ?? "",
                                }))
                              }
                              borderColor={
                                theme.palette.customColors?.greyBorder || "#ddd"
                              }
                              inputHeight="40px"
                              borderRadius="4px"
                            />
                          </Grid2>
                          <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                            <CustomTypography
                              variant="body2"
                              sx={{ mb: 0.75, fontWeight: 500 }}
                            >
                              Declared Value
                            </CustomTypography>
                            <NumericTextField
                              fullWidth
                              size="small"
                              placeholder="Optional"
                              value={bulkEditFields.declared_value}
                              onChange={(event) =>
                                setBulkEditFields((prev) => ({
                                  ...prev,
                                  declared_value: event.target.value,
                                }))
                              }
                              sx={textFieldSx}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>
                    </DialogContent>
                    <DialogActions
                      sx={{
                        padding: { xs: "16px 20px 20px", sm: "20px 24px 24px" },
                        borderTop: `1px solid ${theme.palette.customColors?.greyBorder || "#e0e0e0"}`,
                        backgroundColor:
                          theme.palette.customColors?.lightGreyColor,
                        gap: 2,
                      }}
                    >
                      <Button
                        onClick={handleClosePopup}
                        variant="outlined"
                        disabled={bulkEditSubmitting}
                        sx={{
                          borderColor:
                            theme.palette.customColors?.greyDarkborder,
                          color: theme.palette.customColors?.greyTextColor,
                          fontWeight: 600,
                          padding: "10px 24px",
                          borderRadius: "8px",
                          "&:hover": {
                            borderColor:
                              theme.palette.customColors?.greyTextColor,
                            backgroundColor:
                              theme.palette.customColors?.lightGreyColor,
                          },
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleBulkEditSubmit}
                        disabled={bulkEditSubmitting}
                        sx={{
                          backgroundColor:
                            theme.palette.customColors?.EmeraldGreen ||
                            theme.palette.primary.main,
                          color: theme.palette.customColors?.white || "#fff",
                          fontWeight: 600,
                          padding: "10px 24px",
                          borderRadius: "8px",
                          "&:hover": {
                            backgroundColor:
                              theme.palette.customColors?.greenDark ||
                              theme.palette.primary.dark,
                            transform: "translateY(-1px)",
                            boxShadow: `0 4px 12px ${theme.palette.customColors?.EmeraldGreen || theme.palette.primary.main}30`,
                          },
                        }}
                      >
                        {bulkEditSubmitting ? "Saving…" : "Save"}
                      </Button>
                    </DialogActions>
                  </Dialog>
                )}

                {activePopup === "addStatus" && (
                  <ShipmentStatusPopup
                    open={true}
                    data={Status}
                    onClose={handleClosePopup}
                    onStatusDropdownSelect={(value) =>
                      handlePopupSubmit("status", value)
                    }
                  />
                )}

                {activePopup === "addPacks" && addPacksParams && (
                  <AddPacksPopup
                    open={true}
                    getPacksParams={addPacksParams}
                    onClose={handleClosePopup}
                    onSavePacks={handleSavePacks}
                  />
                )}

                <CreateShipmentWizardPopup
                  open={isCreateShipmentWizardOpen}
                  onClose={() => setIsCreateShipmentWizardOpen(false)}
                  orderId={lockedOrderId}
                  onSuccess={() =>
                    setParams((prev) => ({
                      ...prev,
                      tab_id: "new",
                      status: SHIPMENT_STATUS_TABS.find((t) => t.id === "new")
                        ?.statusIds ?? [ShipmentStatusEnum.NEW],
                      page: 1,
                    }))
                  }
                />

                <TrackingCsvUploadPopup
                  open={isEditCsvDialogOpen}
                  onClose={() => setIsEditCsvDialogOpen(false)}
                  onUpdated={() =>
                    setParams((prevParams) => ({ ...prevParams, page: 1 }))
                  }
                />

                <ShipmentLinesCsvUploadPopup
                  open={isLinesCsvDialogOpen}
                  onClose={() => setIsLinesCsvDialogOpen(false)}
                  onUpdated={() =>
                    setParams((prevParams) => ({ ...prevParams, page: 1 }))
                  }
                />
              </>
            )}
            <EditShipmentModal
              key={editShipmentId ?? "edit-shipment-closed"}
              open={Boolean(editShipmentId)}
              shipmentId={editShipmentId}
              onClose={() => {
                try {
                  setEditShipmentId(null);
                } catch {
                  // User can retry Cancel / X if close state fails.
                }
              }}
            />
            <ShipmentActivityLogsDrawer />
          </Box>
        </Paper>
      ) : (
        <PermissionBlockLabel />
      )}
    </Grid2>
  );
};

export default observer(UniversalShipments);
